import { useState, useEffect, useMemo, useCallback } from 'react';
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import 'moment/locale/pt-br';

import { Stack, Button } from '@mui/material';

/* hooks */
import useFlashMessage from '../../../hooks/useFlashMessage';
import { useNavigateParams } from '../../../hooks/useNavigateParams';

/* api */
import * as TabelaAPI from '../../../api/tabela';

import Input from '../../form/Input';
import TextArea from '../../form/TextArea';
import Checkbox from '../../form/Checkbox';
import SelectEmpresa from '../../form/SelectEmpresa';
import Select from '../../form/Select';

const CHECKBOX_FIELDS = [
    {
        name: 'balanco',
        text: 'Balanço',
        title: 'Operação de balanço atua com entradas ou saídas no estoque, conforme a necessidade do balanço',
    },
    {
        name: 'conferencia',
        text: 'Conferência',
        title: 'Operação de conferências no estoque Entrada e Saída',
    },
    {
        name: 'entrada',
        text: 'Entrada',
        title: 'Define que a operação dar entrada no estoque',
    },
    {
        name: 'industria',
        text: 'Indústria',
        title: 'Define que a operação é utilizada no modulo de indústria',
    },
    {
        name: 'inventario',
        text: 'Inventário',
        title: 'Define que a operação é utilizada no modulo de inventários',
    },
    {
        name: 'locacao',
        text: 'Locação',
        title: 'Define que a operação é utilizada no modulo de locações',
    },
    {
        name: 'manutencao',
        text: 'Manutenção',
        title: 'Define que a operação é de manutenção',
    },
    {
        name: 'saida',
        text: 'Saída',
        title: 'Define que a operação dar sáida no estoque',
    },
    {
        name: 'servico',
        text: 'Serviço',
        title: 'Define que a operação é um serviço',
    },
    {
        name: 'requisitante',
        text: 'Requisição',
        title: 'Define que a operação é uma requisição de produtos ou serviços',
    },
    {
        name: 'arq',
        text: 'Arquivo',
        title: 'Define que a operação finaliza em arquivamento',
    },
    {
        name: 'generico',
        text: 'Genérico',
        title: 'Define que a operação tem contador genérico',
    },
];

function Form({
    token,
    dados,
    setDados,
    empresaId,
    empresaIds,
    parametro,
    handleSubmit,
    btnText,
    caminhoVoltar,
    OptionsEmpresa,
    OptionsOperacao,
    OptionsSituacao,
}) {
    const [enumTipoColaborador, setEnumTipoColaborador] = useState([]);
    const [enumSituacao, setEnumSituacao] = useState([]);

    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    useEffect(() => {
        async function carregarEnums() {
            try {
                const [tipoColaboradorResult, campoSituacaoResult] = await Promise.all([
                    TabelaAPI.enums('Operacao', 'tipoColaborador'),
                    TabelaAPI.enums('Operacao', 'campoSituacao'),
                ]);

                setEnumTipoColaborador(tipoColaboradorResult || []);
                setEnumSituacao(campoSituacaoResult || []);
            } catch (error) {
                console.error('Erro ao carregar enums da operação:', error);
                setEnumTipoColaborador([]);
                setEnumSituacao([]);
            }
        }

        carregarEnums();
    }, []);

    const handleChange = useCallback((e) => {
        const { name, value, checked, type } = e.target;

        setDados((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }, []);

    const handleOnBlur = useCallback(
        async (e) => {
            const { name, value } = e.target;

            if (name === 'descricao' && btnText === 'Cadastrar') {
                try {
                    const duplicidade = await TabelaAPI.duplicidadeDescricao(dados.empresaId, 'Operacao', value);

                    if (duplicidade?.uuid) {
                        setFlashMessage('Registro em duplicidade!', 'warning');
                        navigate(`/Operacao/CreateOrEdit`, {
                            uuid: duplicidade.uuid,
                            btnText: 'Atualizar',
                        });
                    }
                } catch (error) {
                    console.error('Erro ao verificar duplicidade:', error);
                }
            }
        },
        [btnText, dados.empresaId, navigate, setFlashMessage]
    );

    const submit = useCallback(
        (e) => {
            e.preventDefault();

            const payload = {
                ...dados,
                campoSituacao: dados.campoSituacao || document.getElementById('campoSituacao')?.value || '',
            };

            handleSubmit(payload);
        },
        [dados, handleSubmit]
    );

    return (
        <Tabs>
            <form onSubmit={submit}>
                <h1>Operação</h1>

                <TabList>
                    <Tab title="Parâmetros gerais das operações">Operação</Tab>
                    <Tab title="Parâmetros do modulo de estoque">Estoque</Tab>
                    <Tab title="Parâmetros do modulo financeiro">Financeiro</Tab>
                    <Tab title="Parâmetros do modulo indústria">Indústria</Tab>
                </TabList>

                <TabPanel>
                    <div>
                        <SelectEmpresa
                            text="Selecione a Empresa"
                            name="empresaId"
                            options={OptionsEmpresa}
                            handleOnChange={handleChange}
                            handleOnBlur={handleOnBlur}
                            value={dados.empresaId}
                            required
                            notOptNull={true}
                        />

                        <Input
                            text="Descrição"
                            type="text"
                            name="descricao"
                            placeholder="Operação"
                            title="Digite o nome da operação"
                            handleOnChange={handleChange}
                            handleOnBlur={handleOnBlur}
                            value={dados.descricao || ''}
                            required={true}
                        />

                        <Input
                            text="Sigla"
                            type="text"
                            name="sigla"
                            placeholder="Sigla"
                            title="Digite a sigla"
                            handleOnChange={handleChange}
                            handleOnBlur={handleOnBlur}
                            value={dados.sigla || ''}
                            required={true}
                        />

                        <Select
                            name="tipoColaborador"
                            title="Selecione o vinculo com o colaborador"
                            text="Tipo Colaborador"
                            inputWidth="150px"
                            options={enumTipoColaborador}
                            handleOnChange={handleChange}
                            handleOnBlur={handleOnBlur}
                            value={dados.tipoColaborador || ''}
                            isEnum={true}
                            notOptNull={false}
                            required={false}
                        />

                        <Checkbox
                            title="Ativa quando marcado"
                            text="Ativo"
                            name="ativo"
                            handleOnChange={handleChange}
                            value={dados.ativo}
                            checked={!!dados.ativo}
                        />

                        <Checkbox
                            title="determina que essa operação é oficial fiscal/contábil"
                            text="Contábil"
                            name="contabil"
                            handleOnChange={handleChange}
                            value={dados.contabil}
                            checked={!!dados.contabil}
                        />
                    </div>

                    <div>
                        <TextArea
                            text="Aplicação"
                            type="text"
                            name="aplicacao"
                            placeholder="Digite aqui detalhes da operação"
                            title="Aplicação da operação"
                            handleOnChange={handleChange}
                            handleOnBlur={handleOnBlur}
                            value={dados.aplicacao || ''}
                            textAlign="left"
                            linhas="4"
                        />
                    </div>

                    <div>
                        <Select
                            name="campoSituacao"
                            title="Selecione o vinculo com a situação"
                            text="Campo situação"
                            inputWidth="150px"
                            options={enumSituacao}
                            handleOnChange={handleChange}
                            handleOnBlur={handleOnBlur}
                            value={dados.campoSituacao || ''}
                            isEnum={true}
                            notOptNull={true}
                            required={true}
                        />

                        {CHECKBOX_FIELDS.map((item) => (
                            <Checkbox
                                key={item.name}
                                title={item.title}
                                text={item.text}
                                name={item.name}
                                handleOnChange={handleChange}
                                value={dados[item.name]}
                                checked={!!dados[item.name]}
                            />
                        ))}
                    </div>
                </TabPanel>

                <TabPanel>
                    <div />
                </TabPanel>

                <TabPanel>
                    <div />
                </TabPanel>

                <TabPanel>
                    <div />
                </TabPanel>

                <div>
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" color="secondary" onClick={() => navigate(caminhoVoltar)}>
                            Voltar
                        </Button>

                        <Button type="submit" variant="contained" color="success">
                            {btnText}
                        </Button>
                    </Stack>
                </div>
            </form>
        </Tabs>
    );
}

export default Form;
